<template>
    <div class="pattern-editor"
        :class="{'readonly' : readonly, 'multi-line' : multiLine, 'single-line' : !multiLine}"
    >
        <div ref="editable"
             class="editable"
             :contenteditable="!readonly"
             @keydown="handleKeyDown"
             @keyup="handleKeyUp"
             @dragover.prevent
             @drop.prevent
             @focus="handleFocus"
             @blur="handleBlur"
             @mouseup="handleMouseUp"
             @mousedown="handleMouseDown"
             @click="handleClick"
             @input="handleInput"
             v-html="stringifiedValue"
             @paste.stop.prevent="handlePaste"
        ></div>
        <div v-show="showPlaceholder" class="placeholder" @click="focusEditable">{{placeholder}}</div>
        <div class="error-text" v-show="$v.value.$invalid">{{'Incorrect Pattern'}}</div>
    </div>
</template>

<script>
import * as _ from 'lodash'
import mergeTag from './merge_tag'
import rangy from 'rangy'
import textRange from 'rangy/lib/rangy-textrange'
import Vue from 'vue'
import { mapState } from 'vuex'

import { patternValidationMixin } from '@/mixins/pattern-validation.mixin'
import { PATTERNS_STRONG_SYNTAX } from './patter-types'

const ZERO_WIDTH_SPACE = '\u200b'
const NON_BREAKING_SPACE = '\u00a0'
const UPDATE_DEBOUNCE_INTERVAL = 100
const WIDTH_UPDATE_INTERVAL = 200
const DIRECTION = {
  left: 'left',
  right: 'right'
}
const MergeTag = Vue.extend(mergeTag)

/* eslint-disable no-magic-numbers */
export default {

  mixins: [patternValidationMixin],

  computed: {

    ...mapState('knowledgebase/entities', ['allEntities']),
    debounceUpdateValue () {
      return _.debounce(() => this.updateValue(this.getCurrentTextSelection()), UPDATE_DEBOUNCE_INTERVAL)
    },

    editable () {
      return this.$refs.editable
    },

    showPlaceholder () {
      return this.valueIsEmpty && this.placeholder
    }
  },

  mounted () {
    this.stringifiedValue = this.stringifyValue(this.value)
    this.lastKnownValue = this.value
    this.editableWidth = this.getEditableWidth()
    this.$nextTick(() => {
      this.mountMergeTagComponents()
      // TODO: possible performance bottleneck(?)
      this.widthUpdateInterval = setInterval(() => this.updateEditableWidth(), WIDTH_UPDATE_INTERVAL)
      this.fireChangeEvent()
    })
  },

  beforeDestroy () {
    this.debounceUpdateValue.cancel()
    clearInterval(this.widthUpdateInterval)
    _.forEach(this.mergeTags, tag => tag.$destroy())
  },

  data () {
    return {
      mergeTags: [],
      valueIsEmpty: true,
      stringifiedValue: '',
      lastKnownValue: null,
      suppressBlur: false,
      editableWidth: 0,
      widthUpdateInterval: null,
      lastTextSelectionRangy: null
    }
  },

  methods: {
    handleClick () {
      this.suppressBlur = false
    },
    handleMouseDown () {
      this.suppressBlur = true
    },
    updateEditableWidth () {
      const currentWidth = this.getEditableWidth()
      if (currentWidth !== this.editableWidth) {
        this.editableWidth = currentWidth
        _.forEach(this.mergeTags, tag => tag.setMaxWidth(this.editableWidth))
      }
    },

    getEditableWidth () {
      const editableComputedStyles = window.getComputedStyle(this.$refs.editable)
      const width = parseFloat(editableComputedStyles.width)
      const paddingLeft = parseFloat(editableComputedStyles.paddingLeft)
      const paddingRight = parseFloat(editableComputedStyles.paddingRight)
      return width - paddingLeft - paddingRight
    },

    stringifyValue (value) {
      const convertedValue = _.reduce(value, (memo, item) => {
        if (_.isString(item)) {
          // convert < and > to special chars to not be treated as tags
          item = _.chain(item).replace(/</g, '&lt;').replace(/>/g, '&gt;').value()
        }
        if (_.isString(item) && this.multiLine) {
          // convert all spaces to "Non-breaking space" (\u00a0) symbols for consistency
          // and all \n to <br>
          return memo + _.chain(item)
            .replace(/^ /, `${NON_BREAKING_SPACE}`)
            .replace(/ $/, `${NON_BREAKING_SPACE}`)
            .replace(/ {2}/g, ` ${NON_BREAKING_SPACE}`)
            .replace(/ {2}/g, ` ${NON_BREAKING_SPACE}`)
            .replace(/\n/g, `${ZERO_WIDTH_SPACE}<br data-new-line="true">${ZERO_WIDTH_SPACE}`)
            .value()
        } else if (_.isString(item) && !this.multiLine) {
          // convert all spaces to "Non-breaking space" (\u00a0) symbols for consistency
          // and remove all \n
          return memo + _.chain(item)
            .replace(/^ /, `${NON_BREAKING_SPACE}`)
            .replace(/ $/, `${NON_BREAKING_SPACE}`)
            .replace(/ {2}/g, ` ${NON_BREAKING_SPACE}`)
            .replace(/\n/g, '')
            .value()
        } else if (_.isObject(item) && item.strong) {
          return `${memo}<strong>${item.strong}</strong>`
        } else if (_.isObject(item) && _.isObject(item.path)) {
          const data = item.path
          // convert variables to non-editable spans
          // wrap with 'Zero-width space' (\u200b) to fix cursor positioning
          return `${memo}${ZERO_WIDTH_SPACE}<span data-entity="${data.entity}" ${data.role ? `data-role="${data.role}"` : ''} >{}</span>${ZERO_WIDTH_SPACE}`
        }
        return memo
      }, '')
      return convertedValue
    },

    /**
     * Fire 'changed' and 'changed:stats' and inform listeners of how much text symbols and merge tags there
     * currently are in the editable field
     */
    fireChangeEvent () {
      const stats = this.getTextStatistics()
      this.$emit('stats-change', stats)
      this.valueIsEmpty = !stats.characterCount && !stats.mergeTagsCount
    },

    blurEditable () {
      this.editable.blur()
    },

    focusEditable () {
      this.editable.focus()
    },

    /**
     * return current selection range
     * @returns {Range|undefined} selection range
     */
    getCurrentSelectionRange () {
      const selection = rangy.getSelection()
      return selection.rangeCount ? selection.getRangeAt(0) : undefined
    },

    /**
     * Return a text selection object
     * @example
     * {
     *   backward         : false,
     *   characterOptions : undefined,
     *   characterRange   : {
     *     end   : 12
     *     start : 12
     *   }
     * }
     * @returns {Object} text selection object
     */
    getCurrentTextSelection () {
      return textRange.getSelection().saveCharacterRanges(this.editable)[0]
    },

    /**
     * Set caret to the end of the input
     */
    setSelectionToEditableEnd () {
      const editableLength = this.editable.innerText.length
      this.setTextSelection({
        characterRange: {
          end: editableLength,
          start: editableLength
        },
        backward: false,
        characterOptions: undefined
      })
    },

    /**
     * Set selection from text selection object
     * @param {Object} selection selection in a text
     */
    setTextSelection (selection) {
      const textSelection = textRange.getSelection()
      textSelection.restoreCharacterRanges(this.editable, _.isArray(selection) ? selection : [selection])
      this.rememberSelection()
    },

    /**
     * Return info about the number of symbols in text part of the field and number of merge fields used
     * @example
     * {
     *   characterCount : 0,
     *   mergeTagsCount : 0
     * }
     * @returns {Object} statistics data
     */
    getTextStatistics () {
      return _.reduce(this.editable.childNodes, (stats, node) => {
        if (node.nodeType === Node.ELEMENT_NODE && node.dataset) {
          stats.mergeTagsCount += 1
        } else if (node.nodeType === Node.ELEMENT_NODE && node.nodeName === 'BR' && this.multiLine && node.dataset && node.dataset.newLine) { // eslint-disable-line no-dupe-else-if
          stats.characterCount += 1
        } else if (node.nodeType === Node.TEXT_NODE) {
          // strip off 'Zero-width space' (\u200b) and "Non-breaking space" (\u00a0)
          stats.characterCount += _.chain(node.nodeValue).replace(new RegExp(NON_BREAKING_SPACE, 'g'), ' ').replace(new RegExp(ZERO_WIDTH_SPACE, 'g'), '').value().length
        }
        return stats
      }, {
        characterCount: 0,
        mergeTagsCount: 0
      })
    },

    handleInput () {
      this.debounceUpdateValue()
      this.fireChangeEvent()
    },

    handlePaste (event) {
      let text = event.clipboardData.getData('text')
      if (!this.multiLine) {
        text = _.replace(text, /\n/g, '')
      }
      this.insertAtSelection([document.createTextNode(text)])
    },

    handleBlur () {
      if (this.suppressBlur) {
        this.suppressBlur = false
      } else {
        this.$el.classList.remove('active')
        this.$refs.editable.scrollLeft = 0
        this.$emit('blur')
      }
    },

    handleMouseUp () {
      /** Do nothing */
    },

    handleFocus () {
      this.$emit('focus')
      this.$el.classList.add('active')
    },

    handleKeyDown (event) {
      if (this.keyPressed(event, 'Enter')) {
        this.handleEnterKeyDown(event)
      } else if (this.keyPressed(event, 'Backspace')) {
        this.handleBackspaceKeyDown(event)
      } else if (this.keyPressed(event, 'Delete')) {
        this.handleDeleteKeyDown(event)
      } else if (this.keyPressed(event, '{')) {
        this.handleCurlyBracketKeyDown(event)
      } else if (this.keyPressed(event, '(') ||
                    this.keyPressed(event, ')') || this.keyPressed(event, '[') ||
                    this.keyPressed(event, ']') || this.keyPressed(event, '|')
      ) {
        this.handleBracketKeyDown(event.key)
      }

      this.rememberSelection()
    },

    handleKeyUp (event) {
      switch (event.key) {
        case 'Enter': {
          this.handleEnterKeyDown(event)
          break
        }
      }
      this.normalizeSelection(event.key, event.shiftKey)
    },

    handleEnterKeyDown (event) {
      // Looks like it's not needed. But lets same for now, maybe it will be needed.
      // https://gitlab.com/onereach/platform/nlu/nlu-ui/-/issues/385

      // if (this.multiLine) {
      //   const brNode = document.createElement('br')
      //   const leftWrapperNode = document.createTextNode(ZERO_WIDTH_SPACE)
      //   const rightWrapperNode = document.createTextNode(ZERO_WIDTH_SPACE)
      //   brNode.dataset.newLine = true
      //   this.insertAtSelection([leftWrapperNode, brNode, rightWrapperNode])
      // }
      event.preventDefault()
    },

    handleBackspaceKeyDown (event) {
      const selection = this.getCurrentTextSelection()
      const range = selection.characterRange
      const start = range.start
      const end = range.end
      const text = rangy.innerText(this.editable)

      if (start === end &&
                    text[start - 1] === ZERO_WIDTH_SPACE &&
                    text[start - 2] === '}' &&
                    text[start - 3] === '{' &&
                    text[start - 4] === ZERO_WIDTH_SPACE) {
        selection.characterRange.start = start - 4
        this.setTextSelection(selection)
        event.preventDefault()
      } else if (start === end &&
                    text[start - 1] === ZERO_WIDTH_SPACE &&
                    // && text[start - 2] === '\n'
                    text[start - 3] === ZERO_WIDTH_SPACE) {
        selection.characterRange.start = start - 3
        this.setTextSelection(selection)
        this.insertAtSelection()
        event.preventDefault()
      }
    },

    handleBracketKeyDown (key) {
      this.insertBracketKeyDown(key)
      event.preventDefault()
    },

    handleCurlyBracketKeyDown (event) {
      event.preventDefault()
      this.insertAndFocusMergeTagAtSelection()
    },

    insertBracketKeyDown (bracket) {
      this.debounceUpdateValue.cancel()
      this.focusEditable()
      this.$nextTick(() => {
        const range = this.getCurrentSelectionRange()

        range.deleteContents()

        const strong = document.createElement('strong')
        strong.innerText = bracket

        range.insertNode(document.createTextNode(ZERO_WIDTH_SPACE))
        range.insertNode(strong)
        range.insertNode(document.createTextNode(ZERO_WIDTH_SPACE))

        const selection = this.getCurrentTextSelection()
        this.updateValue({
          characterRange: {
            end: selection.characterRange.end + 3,
            start: selection.characterRange.start + 3
          },
          backward: false,
          characterOptions: undefined
        })
        // don't fire off blur event during caret transition from editable to merge-tag
        // this.mountMergeTagComponents();

        this.fireChangeEvent()
      })
    },

    handleDeleteKeyDown (event) {
      const selection = this.getCurrentTextSelection()
      const range = selection.characterRange
      const { start, end } = range.start

      if (start === end) {
        const text = rangy.innerText(this.editable)
        if (text[start] === ZERO_WIDTH_SPACE &&
                      text[start + 1] === '{' &&
                      text[start + 2] === '}' &&
                      text[start + 3] === ZERO_WIDTH_SPACE) {
          selection.characterRange.end = start + 4
          this.setTextSelection(selection)
          event.preventDefault()
        }
      }
    },

    insertAtSelection (nodes) {
      this.focusEditable()
      this.$nextTick(() => {
        if (this.lastTextSelectionRangy) {
          this.setTextSelection(this.lastTextSelectionRangy)
        } else {
          this.setSelectionToEditableEnd()
        }

        const range = this.getCurrentSelectionRange()

        range.deleteContents()
        if (nodes) {
          let nodesLength = 0
          _.forEach(nodes, node => {
            if (node.nodeName === 'BR') {
              // in Firefox innerText of BR is empty
              nodesLength += 1
            } else if (node.nodeType === Node.ELEMENT_NODE) {
              nodesLength += node.innerText.length
            } else if (node.nodeType === Node.TEXT_NODE) {
              nodesLength += node.nodeValue.length
            }

            range.insertNode(node)
          })

          // compensate for ZERO_WIDTH_SPACE wrapping
          const textSelection = this.getCurrentTextSelection()
          textSelection.characterRange.start += nodesLength
          textSelection.characterRange.end = textSelection.characterRange.start
          this.updateValue(textSelection)
        } else {
          const textSelection = this.getCurrentTextSelection()
          textSelection.characterRange.start = textSelection.characterRange.end
          this.setTextSelection(textSelection)
        }
        this.fireChangeEvent()
      })
    },

    // TODO: (1) path in not setable anywhere (2) focus is always "true"
    insertAndFocusMergeTagAtSelection (path, focus = true) {
      this.debounceUpdateValue.cancel()
      this.focusEditable()
      this.$nextTick(() => {
        if (this.lastTextSelectionRangy) {
          this.setTextSelection(this.lastTextSelectionRangy)
        } else {
          const editableLength = this.editable.innerText.length
          this.setTextSelection({
            characterRange: {
              end: editableLength,
              start: editableLength
            },
            backward: false,
            characterOptions: undefined
          })
        }

        const range = this.getCurrentSelectionRange()
        range.deleteContents()

        const mergeTagNode = document.createElement('span')
        mergeTagNode.setAttribute('class', 'variable')
        mergeTagNode.setAttribute('contenteditable', 'false')
        mergeTagNode.dataset.entity = _.get(path, 'entity', '')
        mergeTagNode.dataset.entity = _.get(path, 'role', '')
        mergeTagNode.innerHTML = ''
        if (range.endContainer !== this.editable && range.endContainer.parentNode !== this.editable) {
          rangy.dom.insertAfter(document.createTextNode(ZERO_WIDTH_SPACE), range.endContainer.parentNode)
          rangy.dom.insertAfter(mergeTagNode, range.endContainer.parentNode)
          rangy.dom.insertAfter(document.createTextNode(ZERO_WIDTH_SPACE), range.endContainer.parentNode)
        } else {
          range.insertNode(document.createTextNode(ZERO_WIDTH_SPACE))
          range.insertNode(mergeTagNode)
          range.insertNode(document.createTextNode(ZERO_WIDTH_SPACE))
        }

        let indexFound = false

        const index = _.reduce(this.editable.childNodes, (tagIndex, node) => {
          if (indexFound) {
            return tagIndex
          }
          if (node.tagName === 'SPAN' && node.dataset) {
            if (node === mergeTagNode) {
              indexFound = true
            } else {
              tagIndex += 1
            }
          }
          return tagIndex
        }, 0)

        // don't fire off blur event during caret transition from editable to merge-tag
        this.suppressBlur = true
        this.mountMergeTagComponents()
        if (focus) {
          this.mergeTags[index].focus()
        } else {
          this.blurEditable()
        }

        this.fireChangeEvent()
      })
    },

    keyPressed (event, key) {
      // eslint-disable-next-line no-unused-vars, quote-props
      const keyCodes = {
        ArrowDown: 40,
        ArrowLeft: 37,
        ArrowRight: 39,
        ArrowUp: 38,
        Backspace: 8,
        Delete: 46,
        End: 35,
        Enter: 13,
        Home: 36,
        '{': 123,
        '[': 219,
        ']': 221,
        '(': 57,
        ')': 48
      }
      return event.key === key
    },

    /**
     * Checks whether char should be respected as DOM element (some of the chars are simply needed evil)
     */
    isValidChar (char) {
      return char !== ZERO_WIDTH_SPACE && char !== undefined
    },

    /**
     * Pattern is created in a string as "ZERO_WIDTH_SPACE{}ZERO_WIDTH_SPACE
     * Selector should be just on NON_BREAKING_SPACE to function correctly
     * This & getEndRangeOfTheElement is designed specially to correctly
     * navigate from start|end of the pattern element(in terms of RangeSelector)
     * For more info regarding Rangy see: https://github.com/timdown/rangy/wiki/Text-Range-Module
     */
    getStartRangeOfTheElement (editableText, startRange) {
      let i = startRange
      while (!this.isValidChar(editableText[i]) && i > -1) {
        i--
      }
      let j = i
      let shouldCloseTag = false
      while (!shouldCloseTag && j < editableText.length) {
        const currChar = editableText[j]
        if (currChar === '{') {
          break
        }
        if (currChar === '}') {
          shouldCloseTag = true
        }
        j++
      }
      while (i >= 0 && (shouldCloseTag || !this.isValidChar(editableText[i]))) {
        if (editableText[i] === '{') {
          shouldCloseTag = false
        } else {
          if (editableText[i] === '}') {
            shouldCloseTag = true
          }
          i--
        }
      }
      if (editableText[i] === '{') {
        // -= "ZERO_WIDTH_SPACE"
        i -= 1
      }

      return Math.max(i, 0)
    },

    getEndRangeOfTheElement (editableText, endRange) {
      let i = endRange
      while (!this.isValidChar(editableText[i - 1]) && i < editableText.length - 1) {
        i++
      }
      let shouldCloseTag = false
      let j = i
      while (!shouldCloseTag && j > -1) {
        const currChar = editableText[j]
        if (currChar === '}') {
          break
        }
        if (currChar === '{') {
          shouldCloseTag = true
        }
        j--
      }
      while (i < editableText.length && (shouldCloseTag || !this.isValidChar(editableText[i - 1]))) {
        if (editableText[i] === '}') {
          shouldCloseTag = false
        } else {
          if (editableText[i] === '{') {
            shouldCloseTag = true
          }
          i++
        }
      }
      if (editableText[i] === '}') {
        // += ("ZERO_WIDTH_SPACE" + '}')
        i += 2
      }

      return Math.min(i, editableText.length)
    },

    /**
     * @param pressedKey{String} - key name
     * @param multipleSelection{Boolean} - shift down = true
     */
    normalizeSelection (pressedKey, multipleSelection = false) {
      if (!this.editable.innerText) {
        return
      }
      let direction
      switch (pressedKey) {
        case 'ArrowDown':
        case 'ArrowRight':
        case 'PageDown': {
          direction = DIRECTION.right
          break
        }
        case 'PageUp':
        case 'ArrowUp':
        case 'ArrowLeft': {
          direction = DIRECTION.left
          break
        }
      }

      const editableText = rangy.innerText(this.editable)
      const textSelection = this.getCurrentTextSelection()
      const { start, end } = textSelection.characterRange

      if (direction === DIRECTION.right) {
        const adjustedEndRange = this.getEndRangeOfTheElement(editableText, end)
        if (adjustedEndRange !== end) {
          const newRange = {
            start: Math.max(multipleSelection ? Math.min(adjustedEndRange, start) : adjustedEndRange, 0),
            end: adjustedEndRange
          }
          this.setTextSelection({
            ...textSelection,
            characterRange: newRange
          })
        }
      } else if (direction === DIRECTION.left) {
        const adjustedStartRange = this.getStartRangeOfTheElement(editableText, start)
        if (adjustedStartRange !== start) {
          const newRange = {
            start: adjustedStartRange,
            end: Math.min(multipleSelection ? Math.max(adjustedStartRange, end) : adjustedStartRange, editableText.length)
          }
          this.setTextSelection({
            ...textSelection,
            characterRange: newRange
          })
        }
      }
    },

    /**
     * @param content{Object} - { tag?: string; text: string }
     */
    createElement (content) {
      let element
      if (content.tag) {
        element = document.createElement(content.tag)
        element.innerText = content.text
      } else {
        element = document.createTextNode(content.text)
      }
      return element
    },

    // If we edit some <strong> tag we can get something like "|sometext"
    // "sometext" was added and in fact it should not be part of the strong tag.
    // In this method we try to split "|" & "sometext" into 2+ nodes
    fixStrongTag (node) {
      const upsertNodesContent = []
      let nodeText = node.innerText
      for (let j = 0; j < node.innerText.length; j++) {
        const char = node.innerText[j]
        // Creating list of new nodes
        if (!PATTERNS_STRONG_SYNTAX.includes(char)) {
          if (upsertNodesContent.length !== 0) {
            upsertNodesContent[upsertNodesContent.length - 1].text += char
          } else {
            upsertNodesContent.push({
              text: char
            })
          }
        } else if (upsertNodesContent.length !== 0) {
          upsertNodesContent.push({
            tag: 'strong',
            text: char
          })
        }
        // New text of the initial node(will be updated after end of the loop)
        if (upsertNodesContent.length === 1) {
          nodeText = nodeText.slice(0, j)
        }
      }
      // Updating childNodes
      if (upsertNodesContent.length !== 0) {
        node.innerText = nodeText
        // Non-modified so that we can insertBefore
        const nextNode = node.nextSibling
        for (let i = 0; i < upsertNodesContent.length; i++) {
          const upsertNode = this.createElement(upsertNodesContent[i])
          if (nextNode) {
            nextNode.parentNode.insertBefore(upsertNode, nextNode)
          } else {
            node.parentNode.appendChild(upsertNode)
          }
        }
      }
    },

    // Fixing some inconsistencies is pattern's text representation
    fixNodes () {
      for (let i = 0; i < this.editable.childNodes.length; i++) {
        const node = this.editable.childNodes[i]
        switch (node.tagName) {
          case 'STRONG': {
            this.fixStrongTag(node)
            break
          }
          // Right now we assume that user should not add new-line to a pattern
          case 'BR': {
            node.parentNode.removeChild(node)
            break
          }
        }
      }
    },

    updateValue (selection) {
      this.fixNodes()
      const newValue = _.chain(this.editable.childNodes)
        .map(node => {
          if (node.tagName === 'STRONG') {
            return {
              strong: node.innerText
            }
          } else if (node.nodeType === Node.ELEMENT_NODE && node.dataset) {
            return { path: node.dataset }
          } else if (node.nodeType === Node.ELEMENT_NODE && node.nodeName === 'BR' && this.multiLine && node.dataset && node.dataset.newLine) { // eslint-disable-line no-dupe-else-if
            // Should not happend. See fixNodes():"BR"
            // convert <br> to \n for multi-lined input
            return '\n'
          } else if (node.nodeType === Node.ELEMENT_NODE) {
            // strip off 'Zero-width space' (\u200b) and "Non-breaking space" (\u00a0)
            return _.chain(node.innerText).replace(new RegExp(NON_BREAKING_SPACE, 'g'), ' ').replace(new RegExp(ZERO_WIDTH_SPACE, 'g'), '').value()
          } else if (node.nodeType === Node.TEXT_NODE) {
            // strip off 'Zero-width space' (\u200b) and "Non-breaking space" (\u00a0)
            return _.chain(node.nodeValue).replace(new RegExp(NON_BREAKING_SPACE, 'g'), ' ').replace(new RegExp(ZERO_WIDTH_SPACE, 'g'), '').value()
          } else {
            return ''
          }
        })
        .compact()
        .reduce((memo, item) => {
          const lastItem = _.last(memo)
          if (_.isObject(item)) {
            memo.push(item)
          } else if (_.isString(lastItem)) {
            memo.splice(-1, 1, lastItem + item)
          } else {
            memo.push(item)
          }
          return memo
        }, [])
        .value()

      // remember lastKnownStringifiedValue to not do the
      // unnecessaary editable update and merge-tag mount
      this.lastKnownValue = newValue
      this.$emit('input', newValue)
      console.log({ newValue })
      if (selection) {
        this.$nextTick(() => {
          this.setTextSelection(selection)
        })
      }
    },

    rememberSelection () {
      this.lastTextSelectionRangy = this.getCurrentTextSelection()
    },

    forgetSelection () {
      this.lastTextSelectionRangy = null
    },

    /**
     * Convert all <span data-variable="">{}</span> elements to merge-tag Vue components
     */
    mountMergeTagComponents () {
      // destroy all merge-tag components from the previous convertion
      _.forEach(this.mergeTags, tag => {
        tag.$destroy()
      })
      // clean up the list of merge-tag components
      this.mergeTags = []

      // variable to calculate every tag ending (easier then starting) position in the text
      let currentTextPosition = 0
      _.forEach(this.editable.childNodes, node => {
        // increase currentTextPosition by 1 if we encounter <BR>
        if (node.nodeType === Node.ELEMENT_NODE && node.nodeName === 'BR') {
          currentTextPosition += 1
          // increase currentTextPosition by innerText length if we encounter element node
        } else if (node.nodeType === Node.ELEMENT_NODE) {
          currentTextPosition += node.innerText.length
          // increase currentTextPosition by nodeValue length if we encounter text node
        } else if (node.nodeType === Node.TEXT_NODE) {
          currentTextPosition += node.nodeValue.length
        }
        if (node.tagName === 'STRONG') {
          //
        } else if (node.tagName === 'SPAN' && node.dataset) {
          const tag = new MergeTag({
            el: node,
            propsData: {
              value: { ...node.dataset },
              pattern: this.value,
              allEntities: this.allEntities.value,
              textPosition: currentTextPosition,
              steps: this.steps,
              stepId: this.stepId,
              mergeFields: this.mergeFields,
              readonly: this.readonly,
              maxWidth: this.getEditableWidth()
            }
          })
          // update value on merge tag (since vue 2.0 is no longer auto-reactive)
          tag.$on('input', value => {
            node.dataset.entity = value.entity
            node.dataset.role = value.role
            tag.value = value
          })
          tag.$on('mousedown', () => {
            this.suppressBlur = true
          })
          // reaction on merge-tag 'editing start'/focus
          tag.$on('start-editing', () => {
            this.suppressBlur = true
            // remain "focused"
            this.$el.classList.add('merge-tag-active')
            // inform parent text-expression component that it should remain "focused"
            this.$emit('focus')
          })
          // reaction on merge-tag 'editing end'/blur
          tag.$on('finish-editing', params => {
            // if textPosition is in the event params
            if (params && params.textPosition) {
              // updateValue and remain focused putting caret after merge tag
              this.updateValue({
                characterRange: {
                  end: params.textPosition + 3,
                  start: params.textPosition + 3
                },
                backward: false,
                characterOptions: undefined
              })
            } else {
              // stop being focused
              this.$el.classList.remove('merge-tag-active')
              this.$el.classList.remove('active')
              // inform parent text-expression component that it should not remain "focused"
              if (this.suppressBlur) {
                this.suppressBlur = false
              } else {
                this.$emit('blur')
              }
              this.updateValue()
            }
          })
          this.mergeTags.push(tag)
        }
      })
    }
  },

  props: {
    disableVariables: {
      type: Boolean,
      default: false
    },
    readonly: {
      type: Boolean,
      default: false
    },
    value: Array,
    multiLine: {
      type: Boolean,
      default: true
    },
    placeholder: String,
    stepId: String,
    steps: Array,
    mergeFields: {
      type: Array,
      default () {
        return []
      }
    }
  },

  watch: {
    value (newValue) {
      const newKnownValue = _.map(newValue, item => {
        if (_.isObject(item) && item.path) {
          return { path: item.path }
        } else if (_.isObject(item) && item.strong) {
          return { strong: item.strong }
        }
        return item
      })

      if (!_.isEqual(newKnownValue, this.lastKnownValue)) {
        const stringified = this.stringifyValue(newValue)
        // fixes a weird behaviour of vue when editable
        // html does not always change after this.stringifiedValue is changed
        // see #8079
        this.stringifiedValue = `${stringified}  `
        this.$nextTick(() => {
          this.stringifiedValue = stringified
          this.lastKnownValue = newValue
          this.$nextTick(() => {
            this.mountMergeTagComponents()
            // value changed externally, caret position is no longer valid
            // and might be out of bounds
            this.forgetSelection()
            this.fireChangeEvent()
          })
        })
      }
    },

    readonly () {
      this.mountMergeTagComponents()
    }
  }
}
/* eslint-enable no-magic-numbers */
</script>

<style lang="scss" rel="stylesheet/scss">

    .pattern-editor {
        cursor: text;
        position: relative;
        border-radius: 3px;
        transition: border-color 0.5s ease-out, background-color 0.5s ease-out;
        .error-text{
            padding:0 8px ;
        }
        .editable {
            font-size: 14px;
            text-align: left;
            padding: 8px;
            min-height: 36px;
            line-height: 22px;
            outline: none;
            overflow: hidden;
            user-select: text;
            word-break:break-all;
        }

        .placeholder {
            color: #92969d;
            position: relative;
            margin-top: -25px;
            margin-left: 7px;
            padding: 0;
            font-size: 14px;
            overflow: hidden;
            white-space: nowrap;
            text-overflow: ellipsis;
        }

        &.readonly {
            cursor: default;

            .editable {
                min-height: 36px;
                overflow-wrap: anywhere;

                .merge-tag {
                    cursor: default;
                    opacity: 0.38;
                }
            }
        }

        &.single-line {
            white-space: nowrap;
        }
    }
</style>
